const readText = (value) => (typeof value === "string" ? value.trim() : "");

const readList = (value) => (Array.isArray(value) ? value : []);

export const coSponsorAnchorId = (offering) =>
  `co-${String(offering)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}`;

export const normalizeSponsors = (payload) => {
  const raw = payload || {};

  const coSponsors = readList(raw.coSponsors)
    .filter((entry) => entry && readText(entry.offering) !== "")
    .map((entry) => {
      const offering = readText(entry.offering);
      const sponsorNames = readList(entry.sponsorNames).filter(
        (name) => readText(name) !== ""
      );
      return {
        offering,
        anchorId: coSponsorAnchorId(offering),
        sponsorNames,
        sponsorCount: sponsorNames.length,
        open: sponsorNames.length === 0,
      };
    });

  const anchorByOffering = coSponsors.reduce((acc, entry) => {
    acc[entry.offering] = entry.anchorId;
    return acc;
  }, {});

  const fullSponsors = readList(raw.fullSponsors)
    .filter((entry) => entry && readText(entry.offering) !== "")
    .map((entry) => {
      const offering = readText(entry.offering);
      const coSponsored = entry.coSponsored === true;
      return {
        offering,
        coSponsored,
        sponsorName: coSponsored ? "" : readText(entry.sponsorName),
        coAnchorId: coSponsored ? anchorByOffering[offering] || "" : "",
      };
    });

  return {
    year: typeof raw.year === "number" ? raw.year : null,
    fullSponsors,
    coSponsors,
  };
};

export const marqueeEntries = (data) => {
  if (data == null) {
    return [];
  }
  return data.fullSponsors
    .filter((entry) => !entry.coSponsored && entry.sponsorName !== "")
    .map((entry) => ({
      sponsorName: entry.sponsorName,
      offering: entry.offering,
    }));
};

const contains = (value, needle) =>
  String(value || "")
    .toLowerCase()
    .indexOf(needle) !== -1;

export const filterSponsors = (data, query) => {
  const needle = String(query || "")
    .trim()
    .toLowerCase();
  if (data == null || needle === "") {
    return data;
  }
  return {
    ...data,
    fullSponsors: data.fullSponsors.filter(
      (entry) =>
        contains(entry.offering, needle) || contains(entry.sponsorName, needle)
    ),
    coSponsors: data.coSponsors.filter(
      (entry) =>
        contains(entry.offering, needle) ||
        entry.sponsorNames.some((name) => contains(name, needle))
    ),
  };
};

/**
 * The Pujo season a date belongs to. A season is named for the year its Durga Puja falls in and
 * runs on into the next calendar year — Lakshmi and Kali in Oct/Nov, Saraswati in Jan/Feb — so
 * January to March still belongs to the year before. April is the boundary, matching the fiscal
 * year the orders are tagged with.
 */
export const currentPujoYear = (now) => {
  const date = now instanceof Date ? now : new Date();
  return date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
};

const isTaken = (product) =>
  readText(product.sponsoredBy) !== "" || product.coSponsored === true;

/**
 * Builds the sponsor lists for the running year out of the live products, which is the only place
 * the current season exists — the /sponsors/{year} archive is curated by hand once a season ends.
 *
 * Two things the archive has and this cannot: co-sponsor names, which live on orders rather than on
 * the product, and the sponsor's email, which this endpoint does expose but which has no business on
 * a public page. Neither is read here.
 */
export const sponsorsFromProducts = (products, year) => {
  const list = readList(products).filter(
    (product) => product != null && readText(product.name) !== ""
  );

  const fullSponsors = list
    .filter((product) => isTaken(product) && product.coSponsored !== true)
    .map((product) => ({
      offering: readText(product.name),
      coSponsored: false,
      sponsorName: readText(product.sponsoredBy),
      coAnchorId: "",
      amount: product.price,
    }))
    .sort((a, b) => (b.amount || 0) - (a.amount || 0));

  const coSponsors = list
    .filter((product) => product.coSponsored === true)
    .map((product) => {
      const offering = readText(product.name);
      return {
        offering,
        anchorId: coSponsorAnchorId(offering),
        sponsorNames: [],
        sponsorCount: 0,
        // Taken, but by whom is not public for the running year — the card says so rather than
        // inviting someone to sponsor something that is already spoken for.
        open: false,
        namesWithheld: true,
        amount: product.coPrice,
      };
    })
    .sort((a, b) => String(a.offering).localeCompare(String(b.offering)));

  return { year, fullSponsors, coSponsors, live: true };
};
